import {useContext, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {DisplayMediumTypography, StandardTypography} from 'patient-ping-remedy/packages/typography';
import {ScheduleAppointmentJourneyResponse} from '@/api/dto/care-journey';
import {MixpanelEventType, MixpanelTaskEvent} from '@/api/dto/mixpanel';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {FilterInvalidate} from '@common/FilterComponent';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import {useMixpanel} from '@/hooks/useMixpanel';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {Content, Section} from '@/components/pages/tasks/task_edit/progress/TaskEditDetails.styles';
import {
  TERMINAL_APPOINTMENT_STATUSES
} from '@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants';
import {
  SectionContent,
  SectionHeaderRow,
  SectionTitleRow
} from '@/components/pages/tasks/task_edit/progress/CareNoteSection.styles';
import {
  AssignmentState,
  AssignmentStateIcon,
  CancelButton,
  ConfirmUnassignModal,
  ModalContent,
  SectionDescription,
  SectionLabel,
  UnassignButton
} from '@/components/pages/tasks/task_edit/progress/bh_journey/AppointmentAiAssignment.styles';

type Props = {
  careJourneyDetails: ScheduleAppointmentJourneyResponse;
  disabled: boolean;
  invalidate: FilterInvalidate;
}



export default function AppointmentAiAssignment(
  {careJourneyDetails, disabled, invalidate}: Props) {
  const {carecoApi} = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const {addAlert} = useAlertStore();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {mutate: unassignAi, isPending} = useMutation({
    mutationFn: () =>
      carecoApi!.unassignAI(careJourneyDetails.careJourneyInstanceId, currentRoster!.id),
    onSuccess() {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'AI agent unassigned from this appointment.',
      });

      if (mixpanelEventProperties && currentRoster) {
        const event: MixpanelTaskEvent = {
          eventName: MixpanelEventType.BIH_UNASSIGN_AI_FROM_TASK,
          eventType: careJourneyDetails.eventType,
          careJourneyName: careJourneyDetails.journeyType,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster),
        };
        carecoApi?.postMixpanelEvent(event);
      }

      setShowConfirmModal(false);
      invalidate();
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to unassign AI. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  if (TERMINAL_APPOINTMENT_STATUSES.has(careJourneyDetails.status?.toLowerCase())) {
    return null;
  }

  const assignedToAi = careJourneyDetails.userAssignment.assignedToAi;

  const modalButtons = [
    <span key="spacer" />,
    <CancelButton
      key="cancel"
      styleType={StyleType.TERTIARY}
      onClick={() => setShowConfirmModal(false)}
    >
      Cancel
    </CancelButton>,
    <Button key="confirm" onClick={() => unassignAi()} disabled={isPending}>
      {isPending ? 'Unassigning...' : 'Unassign AI'}
    </Button>,
  ];

  return (
    <Content>
      <Section>
        <SectionContent>
          <SectionHeaderRow>
            <div>
              <SectionLabel>AI assignment</SectionLabel>
              <SectionDescription>
                Unassign the AI agent to complete the remaining appointment steps yourself
              </SectionDescription>
            </div>
            <SectionTitleRow>
              {assignedToAi ? (
                <UnassignButton
                  onClick={() => setShowConfirmModal(true)}
                  styleType={StyleType.SECONDARY}
                  disabled={disabled || isPending}
                >
                  Unassign AI
                </UnassignButton>
              ) : (
                <AssignmentState>
                  <AssignmentStateIcon iconClass={ICONS['check']} weight="far" iconSize="1x" />
                  <StandardTypography>Assigned to care team</StandardTypography>
                </AssignmentState>
              )}
            </SectionTitleRow>
          </SectionHeaderRow>
        </SectionContent>
      </Section>

      {showConfirmModal && (
        <ConfirmUnassignModal
          headerText={<DisplayMediumTypography>Unassign AI agent</DisplayMediumTypography>}
          hideModal={() => setShowConfirmModal(false)}
          buttons={modalButtons}
        >
          <ModalContent>
            <Banner type={BannerTypes.INFORMATIONAL}>
              This action cannot be undone
            </Banner>
            <StandardTypography>
              The AI agent will stop working this appointment and cannot be reassigned to it. Your
              care team becomes responsible for the remaining steps.
            </StandardTypography>
          </ModalContent>
        </ConfirmUnassignModal>
      )}
    </Content>
  );
}
